// ============================================================
// ChartOverlay - the four forecast charts, summoned.
//
// The stat strip keeps the numbers on screen; this is the ink. All
// four charts at once in a grid - the carousel's one-at-a-time was a
// space compromise the overlay does not need to make.
// ============================================================
import { useEffect } from 'react';
import type { ProbabilityResult } from '../../engine/probability';
import HitProbabilityChart from '../charts/HitProbabilityChart';
import DamageDistributionChart from '../charts/DamageDistributionChart';
import CritThresholdChart from '../charts/CritThresholdChart';
import WoundsToKillChart from '../charts/WoundsToKillChart';
import styles from '../../styles/StatStrip.module.css';
import useModalBackdrop from '../../hooks/useModalBackdrop';

interface Props {
  result: ProbabilityResult;
  onClose: () => void;
}

export default function ChartOverlay({ result, onClose }: Props) {
  const backdrop = useModalBackdrop(onClose);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className={styles.overlay} data-testid="chart-overlay" role="presentation" onClick={backdrop.onBackdropClick}>
      <div className={styles.overlayBody} ref={backdrop.boxRef} role="dialog" aria-label="Forecast charts">
        <div className={styles.overlayHeader}>
          <span>Forecast</span>
          <button type="button" className={styles.closeBtn} aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={styles.chartGrid}>
          <div className={styles.chartCell}>
            <div className={styles.chartTitle}>Hit Probability</div>
            <HitProbabilityChart data={result.hits} />
          </div>
          <div className={styles.chartCell}>
            <div className={styles.chartTitle}>Damage Distribution</div>
            <DamageDistributionChart data={result.damage} />
          </div>
          <div className={styles.chartCell}>
            <div className={styles.chartTitle}>Critical Threshold</div>
            <CritThresholdChart data={result.crits} />
          </div>
          <div className={styles.chartCell}>
            <div className={styles.chartTitle}>Wounds to Kill</div>
            <WoundsToKillChart data={result.woundsToKill} />
          </div>
        </div>
      </div>
    </div>
  );
}
